import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar, CalendarDays, Clock, User, MapPin, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import Navigation from "./Navigation";
import { meetingApi, Meeting, clientApi, Client } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";

const CalendarPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, clientName, isLoading, sessionId } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [client, setClient] = useState<Client | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const daysOfWeek = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  // Get client name from authentication context
  const getClientName = () => {
    return clientName; // Use the authenticated client name from AuthContext
  };

  // Redirect to auth if not authenticated (only after loading is complete)
  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate('/auth');
    }
  }, [isAuthenticated, isLoading, navigate]);

  // Fetch client data and their meetings
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;
    
    const clientName = getClientName();
    console.log(`Calendar loading for client: ${clientName}, session: ${sessionId}`);
    if (!clientName) {
      setError('No client information found');
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // Fetch client data
    clientApi.getAll()
      .then(clients => {
        const foundClient = clients.find(c => 
          c.name.trim().toLowerCase() === clientName.trim().toLowerCase()
        );
        
        if (foundClient) {
          setClient(foundClient);
          
          // Fetch meetings for this specific client using the API filter
          return meetingApi.getAll({ client: foundClient._id });
        } else {
          setError('Client not found');
          return Promise.resolve([]);
        }
      })
      .then(clientMeetings => {
        console.log('Meetings fetched for client:', getClientName(), 'Found:', clientMeetings.length);
        setMeetings(clientMeetings);
      })
      .catch(err => {
        console.error('Error fetching client or meetings:', err);
        setError('Failed to load calendar data');
      })
      .finally(() => {
        setLoading(false);
      });
  }, [isAuthenticated, clientName]);

  const getEventTypeColor = (type: string) => {
    switch (type) {
      case "gym": return "bg-primary text-primary-foreground";
      case "meeting": return "bg-accent text-accent-foreground";
      case "consultation": return "bg-warning text-warning-foreground";
      case "assessment": return "bg-destructive text-destructive-foreground";
      default: return "bg-secondary text-secondary-foreground";
    }
  };

  const navigateMonth = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate);
    if (direction === "prev") {
      newDate.setMonth(newDate.getMonth() - 1);
    } else {
      newDate.setMonth(newDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();

    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const hasEvents = (date: Date | null) => {
    if (!date) return false;
    return meetings.some(meeting => {
      const meetingDate = new Date(meeting.startTime);
      return meetingDate.getDate() === date.getDate() &&
             meetingDate.getMonth() === date.getMonth() &&
             meetingDate.getFullYear() === date.getFullYear();
    });
  };

  const getEventsForDate = (date: Date | null) => {
    if (!date) return [];
    return meetings.filter(meeting => {
      const meetingDate = new Date(meeting.startTime);
      return meetingDate.getDate() === date.getDate() &&
             meetingDate.getMonth() === date.getMonth() &&
             meetingDate.getFullYear() === date.getFullYear();
    });
  };

  const formatMeetingTime = (dateString: string | Date) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const formatMeetingDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const days = getDaysInMonth(currentDate);

  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      <div className="max-w-7xl mx-auto p-4 md:p-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">Your Calendar</h1>
          <p className="text-muted-foreground">
            {loading ? 'Loading your calendar...' : 
             error ? 'Error loading calendar data' :
             client ? `Calendar for ${client.name}` :
             'Track your training schedule and appointments'}
          </p>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12">
            <div className="flex items-center space-x-2">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
              <span className="text-muted-foreground">Loading your calendar...</span>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 mb-8">
            <div className="flex items-center space-x-2 text-destructive">
              <span className="text-sm font-medium">{error}</span>
            </div>
          </div>
        )}

        {!loading && !error && (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          {/* Calendar View */}
          <div className="xl:col-span-2">
              <Card className="h-fit">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center space-x-2">
                    <CalendarDays className="w-5 h-5 text-primary" />
                    <span>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</span>
                  </CardTitle>
                  <div className="flex space-x-2">
                    <Button variant="outline" size="sm" onClick={() => navigateMonth("prev")}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => navigateMonth("next")}>
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {daysOfWeek.map(day => (
                    <div key={day} className="p-2 text-center font-medium text-muted-foreground text-sm">
                      {day}
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {days.map((day, index) => (
                    <div
                      key={index}
                      className={`aspect-square p-1 text-sm flex flex-col items-center justify-center rounded-lg transition-colors ${
                        day
                          ? hasEvents(day)
                            ? "bg-primary/10 text-primary font-medium cursor-pointer hover:bg-primary/20"
                            : "hover:bg-secondary/50 cursor-pointer"
                          : ""
                      }`}
                    >
                      {day && (
                        <>
                          <span>{day.getDate()}</span>
                          {hasEvents(day) && (
                            <div className="w-1 h-1 bg-primary rounded-full mt-1"></div>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Upcoming Events */}
          <div>
              <Card className="h-fit">
              <CardHeader>
                <CardTitle className="flex items-center space-x-2">
                  <Calendar className="w-5 h-5 text-primary" />
                    <span>Upcoming Meetings</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                  {meetings.length === 0 ? (
                    <div className="text-center py-8">
                      <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-muted-foreground mb-2">
                        No Meetings Scheduled
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        You don't have any meetings scheduled yet.
                      </p>
                    </div>
                  ) : (
                <div className="space-y-4">
                      {meetings.map((meeting) => (
                        <div key={meeting._id} className="p-4 rounded-lg border border-border hover:bg-secondary/50 transition-colors">
                      <div className="flex items-start justify-between mb-2">
                            <h4 className="font-medium text-foreground">{meeting.title}</h4>
                            <Badge className={getEventTypeColor(meeting.type || 'meeting')}>
                              {meeting.type || 'meeting'}
                        </Badge>
                      </div>
                      <div className="space-y-1 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-2">
                          <Clock className="w-3 h-3" />
                              <span>{formatMeetingTime(meeting.startTime)} - {formatMeetingDate(meeting.startTime)}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <User className="w-3 h-3" />
                              <span>Meeting</span>
                            </div>
                            {meeting.notes && (
                              <p className="text-xs text-muted-foreground mt-2">{meeting.notes}</p>
                            )}
                        </div>
                        </div>
                      ))}
                    </div>
                  )}
              </CardContent>
            </Card>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

export default CalendarPage;